/**
 * The visitor's analytics choice, shared by every tool that measures.
 *
 * One choice, kept in one place, applies to Google Analytics and PostHog
 * alike: a visitor who says no is not measured by either. Until they choose,
 * the EU, the UK and Switzerland start declined, as the law there requires;
 * everyone else starts allowed and can turn it off at any time.
 */
export type Consent = "granted" | "denied";

export const CONSENT_KEY = "antar.analytics-consent";

/** The EEA, the UK and Switzerland, as Google's consent mode names them. */
export const STRICT_CONSENT_REGIONS: readonly string[] = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU",
  "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "IS", "LI", "NO", "GB", "CH",
];

/**
 * Time zones outside `Europe/` that belong to the strict regions: the Canary
 * Islands, Madeira, the Azores, the Faroes, Iceland and Svalbard.
 */
const STRICT_ATLANTIC_ZONES = new Set([
  "Atlantic/Canary", "Atlantic/Madeira", "Atlantic/Azores", "Atlantic/Faroe", "Atlantic/Reykjavik", "Arctic/Longyearbyen",
]);

/**
 * Whether a visitor in this time zone waits to be asked before anything is
 * measured. Google decides this for its own tag from the visitor's IP; a
 * static page has no IP to read, so PostHog decides from the time zone.
 * Every `Europe/` zone counts, which over-asks a little (Moscow, Istanbul)
 * rather than under-asks anyone. An unknown zone is asked too.
 */
export function waitsForConsent(timeZone: string | undefined): boolean {
  if (!timeZone) return true;
  return timeZone.startsWith("Europe/") || STRICT_ATLANTIC_ZONES.has(timeZone);
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readConsent(storage: StorageLike | null): Consent | null {
  try {
    const value = storage?.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function writeConsent(storage: StorageLike | null, consent: Consent): void {
  try {
    storage?.setItem(CONSENT_KEY, consent);
  } catch {
    // A private window: the choice still applies to this visit.
  }
}
